import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { adminCmsContentDeleteAction, adminCmsContentListAction } from "../Action/action";
import DeleteConfirmModal from "./delete-confirm-modal";

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  const s = String(value);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function plainText(html) {
  return String(html || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function truncate(text, max) {
  const s = String(text || "");
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

export default function CmsContent() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [reloadNonce, setReloadNonce] = useState(0);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await adminCmsContentListAction();
        if (cancelled) return;
        if (!res?.success) {
          setRows([]);
          setError(res?.message || res?.msg || "Failed to load CMS content.");
          return;
        }
        setRows(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (cancelled) return;
        setRows([]);
        setError(e?.response?.data?.message || e?.message || "Failed to load CMS content.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadNonce]);

  const viewRows = useMemo(() => {
    return (Array.isArray(rows) ? rows : []).map((r) => ({
      raw: r,
      id: r?.id,
      contentKey: r?.content_key || "—",
      title: r?.title || "—",
      shortDescription: truncate(plainText(r?.short_description), 80) || "—",
      active: r?.active !== undefined && r?.active !== null ? !!Number(r.active) : true,
      created: formatDate(r?.created_at)
    }));
  }, [rows]);

  const openDelete = (row) => {
    if (!row?.id) return;
    setDeleteRow(row);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteRow?.id) return;
    setDeleting(true);
    try {
      const res = await adminCmsContentDeleteAction(deleteRow.id);
      if (!res?.success) throw new Error(res?.message || res?.msg || "Delete failed");
      toast.success("CMS content deleted", { toastId: "cms-content-delete" });
      setDeleteOpen(false);
      setDeleteRow(null);
      setReloadNonce((n) => n + 1);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Delete failed", { toastId: "cms-content-delete" });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="content">
      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>CMS Content unavailable.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="toolbar">
        <div className="flex items-center gap-3">
          <h3 style={{ margin: 0 }}>CMS Content</h3>
        </div>
        <Link to="/cms-content/new" className="btn btn-primary">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 5v14" />
            <path d="M5 12h14" />
          </svg>
          Add CMS Content
        </Link>
      </div>

      <div className="card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Content Key</th>
                <th>Title</th>
                <th>Short Description</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6}>
                    <div className="empty-state" style={{ padding: "28px 12px" }}>
                      Loading…
                    </div>
                  </td>
                </tr>
              ) : (
                viewRows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <code>{r.contentKey}</code>
                    </td>
                    <td>
                      <strong>{r.title}</strong>
                    </td>
                    <td>{r.shortDescription}</td>
                    <td>
                      <span className={`badge ${r.active ? "badge-success" : "badge-gray"}`}>
                        {r.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>{r.created}</td>
                    <td>
                      <div className="actions-cell">
                        <Link to={`/cms-content/${r.id}/edit`} className="action-link">
                          Edit
                        </Link>
                        <button
                          type="button"
                          className="action-link text-danger"
                          onClick={() => openDelete(r.raw)}
                          style={{ background: "transparent", border: "none", padding: 0 }}
                        >
                          Delete
                        </button>
                    
                      </div>
                    </td>
                  </tr>
                ))
              )}
              {!loading && viewRows.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <div className="empty-state">
                      <div className="empty-icon">📄</div>
                      No CMS content yet.
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      <DeleteConfirmModal
        open={deleteOpen}
        itemType="CMS content"
        itemName={deleteRow?.title || deleteRow?.content_key || "CMS Content"}
        onCancel={() => {
          if (deleting) return;
          setDeleteOpen(false);
          setDeleteRow(null);
        }}
        onConfirm={confirmDelete}
        confirmDisabled={deleting}
      />
    </div>
  );
}
